import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { VersionedTransaction } from '@solana/web3.js';
import bs58 from 'bs58';
import { OverallRiskCategory } from '../utils/riskUtils';
import { useAuth } from '../contexts/AuthContext';
import { getApiHost, apiFetch } from '../utils/api';
import { redirectToPhantomIfMobile, buildWalletBounceUrl } from '../utils/phantomMobile';
import {
  persistentPhantomWalletMatches,
  startPhantomSignTransaction,
  consumePhantomTxResult,
} from '../utils/phantomDeeplink';
import {
  hasLiveWCSessionSync,
  signTransactionViaWC,
} from '../utils/walletConnectMobile';

const SOL_MINT = 'So11111111111111111111111111111111111111112';

interface OrderResponse {
  requestId: string;
  transaction: string;
  outAmount: string;
  inAmount: string;
  priceImpactPct?: string;
  routePlan?: Array<{
    swapInfo: {
      label?: string;
    };
    percent: number;
  }>;
}

interface SwapPanelProps {
  tokenMint: string;
  tokenSymbol: string;
  tokenDecimals?: number;
  riskLevel?: OverallRiskCategory;
  /** Tighter padding/margins for the mobile fullscreen-trade drawer
   *  where vertical space is at a premium. */
  compact?: boolean;
}

type SwapMode = 'buy' | 'sell';

const SwapPanel: React.FC<SwapPanelProps> = ({
  tokenMint,
  tokenSymbol,
  tokenDecimals = 6,
  riskLevel,
  compact = false,
}) => {
  const { publicKey, signTransaction } = useWallet();

  const { setVisible: setWalletModalVisible } = useWalletModal();
  const { user } = useAuth();
  const authWalletAddress = user?.walletAddress ?? null;

  const [tokenBalance, setTokenBalance] = useState<number | null>(null);

  const [mode, setMode] = useState<SwapMode>('buy');
  const [amount, setAmount] = useState<string>('');
  const [slippageBps, setSlippageBps] = useState<number | 'auto'>('auto');
  // On desktop default to expanded — the right-column SwapPanel has
  // lots of vertical room next to the chart, so showing the slippage
  // options up-front fills the void without an extra click. Compact
  // (mobile drawer) keeps it collapsed since vertical space is tight.
  const [showSlippage, setShowSlippage] = useState(!compact);
  const [quote, setQuote] = useState<OrderResponse | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapStatus, setSwapStatus] = useState<'idle' | 'signing' | 'sending' | 'success' | 'error'>('idle');
  const [txSignature, setTxSignature] = useState<string | null>(null);
  const [swapError, setSwapError] = useState<string | null>(null);

  const isRisky = riskLevel === 'RISKY' || riskLevel === 'DANGEROUS';

  // Determine input/output mints based on mode
  const inputMint = mode === 'buy' ? SOL_MINT : tokenMint;
  const outputMint = mode === 'buy' ? tokenMint : SOL_MINT;
  const inputSymbol = mode === 'buy' ? 'SOL' : tokenSymbol;
  const outputSymbol = mode === 'buy' ? tokenSymbol : 'SOL';
  const inputDecimals = mode === 'buy' ? 9 : tokenDecimals;
  const outputDecimals = mode === 'buy' ? tokenDecimals : 9;

  // Fetch token balance for sell mode. Source the wallet address from
  // either the wallet adapter (desktop / extension) or the user's auth
  // wallet (mobile, signed in via deeplink/WC) so the sell-side
  // percentage presets work for both.
  useEffect(() => {
    const wallet = publicKey?.toBase58() ?? authWalletAddress;
    if (!wallet || mode !== 'sell') {
      setTokenBalance(null);
      return;
    }

    const fetchBalance = async () => {
      try {
        const resp = await apiFetch(
          `${getApiHost()}/api/jupiter/balance?wallet=${wallet}&mint=${tokenMint}`
        );
        if (resp.ok) {
          const data = await resp.json();
          setTokenBalance(data.balance ?? 0);
        } else {
          setTokenBalance(0);
        }
      } catch {
        setTokenBalance(0);
      }
    };

    fetchBalance();
  }, [publicKey, authWalletAddress, tokenMint, mode]);

  const sellPresets = tokenBalance != null && tokenBalance > 0
    ? [
        { label: '25%', value: (tokenBalance * 0.25).toString() },
        { label: '50%', value: (tokenBalance * 0.50).toString() },
        { label: '100%', value: tokenBalance.toString() },
      ]
    : [];

  const presets = mode === 'buy'
    ? [{ label: '0.1 SOL', value: '0.1' }, { label: '0.5 SOL', value: '0.5' }, { label: '1 SOL', value: '1' }]
    : sellPresets;

  // Fetch quote.
  //
  // `silent` skips the loading spinner + error reset and keeps the
  // previously-displayed quote on screen while the request is in
  // flight. Used by the periodic 5s refresh so the UI doesn't flicker
  // through a "Fetching quote…" spinner every refresh tick. The
  // initial debounced fetch (after typing) keeps the visible loading
  // state since there's nothing to fall back to.
  const fetchQuote = useCallback(async (silent = false) => {
    if (!amount || parseFloat(amount) <= 0) {
      setQuote(null);
      return;
    }

    const rawAmount = Math.floor(parseFloat(amount) * Math.pow(10, inputDecimals));
    if (isNaN(rawAmount) || rawAmount <= 0) {
      setQuote(null);
      return;
    }

    if (!silent) {
      setQuoteLoading(true);
      setQuoteError(null);
    }

    try {
      // Taker can come from the wallet adapter (desktop / extension) or
      // from the user's auth wallet (mobile, signed in via deeplink/WC
      // but no adapter connected). With a taker we hit /order to get a
      // signable transaction sized to that taker; without one we hit
      // /quote for a price-only preview so the panel can show "you'd
      // get X" before the user connects. The quote shape (outAmount,
      // priceImpactPct, routePlan) is identical between the two; the
      // /order response additionally includes requestId + transaction,
      // which fetchFreshOrder re-fetches with /order at sign time.
      const taker = publicKey?.toBase58() ?? authWalletAddress;
      const path = taker ? '/api/jupiter/order' : '/api/jupiter/quote';
      const url = new URL(`${getApiHost()}${path}`);
      url.searchParams.set('inputMint', inputMint);
      url.searchParams.set('outputMint', outputMint);
      url.searchParams.set('amount', rawAmount.toString());
      if (taker) url.searchParams.set('taker', taker);
      if (slippageBps !== 'auto') {
        url.searchParams.set('slippageBps', slippageBps.toString());
      }

      const response = await apiFetch(url.toString());

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `API error: ${response.status}`);
      }

      const data: OrderResponse = await response.json();
      setQuote(data);
      // A successful silent refresh also clears any stale error
      // banner the visible fetch may have left behind.
      if (silent) setQuoteError(null);
    } catch (err: any) {
      console.error('Quote fetch error:', err);
      // On a silent refresh, keep the old quote on screen — a
      // transient blip shouldn't wipe the user's price reference.
      if (!silent) {
        setQuoteError(err.message || 'Failed to fetch quote');
        setQuote(null);
      }
    } finally {
      if (!silent) setQuoteLoading(false);
    }
  }, [publicKey, authWalletAddress, amount, inputMint, outputMint, inputDecimals, slippageBps]);

  // Debounced quote fetch. Triggers on amount/mint/slippage changes
  // alone — no wallet required, since fetchQuote falls back to the
  // /quote (price-only) endpoint when there's no taker.
  useEffect(() => {
    const timer = setTimeout(() => { fetchQuote(); }, 600);
    return () => clearTimeout(timer);
  }, [fetchQuote]);

  // Periodic refresh: while the user has a valid amount entered,
  // re-fetch every 5s so the displayed quote doesn't go stale. Skip
  // entirely when amount is empty so we don't hammer Jupiter on idle
  // SwapPanels. `silent: true` keeps the previous quote on screen
  // during the request — no visible "Fetching…" flicker every 5s.
  useEffect(() => {
    if (!amount || parseFloat(amount) <= 0) return;
    const tick = setInterval(() => { fetchQuote(true); }, 5_000);
    return () => clearInterval(tick);
  }, [amount, fetchQuote]);

  // Reset when mode changes
  useEffect(() => {
    setQuote(null);
    setQuoteError(null);
    setSwapStatus('idle');
    setSwapError(null);
    setTxSignature(null);
    setAmount('');
  }, [mode]);

  // Fetch a fresh Jupiter order with the wallet as taker. The debounced
  // quote may be stale by the time the user clicks; this re-fetches so
  // the blockhash + route are current at sign time.
  const fetchFreshOrder = useCallback(async (takerAddress: string): Promise<OrderResponse> => {
    const rawAmount = Math.floor(parseFloat(amount) * Math.pow(10, inputDecimals));
    const freshUrl = new URL(`${getApiHost()}/api/jupiter/order`);
    freshUrl.searchParams.set('inputMint', inputMint);
    freshUrl.searchParams.set('outputMint', outputMint);
    freshUrl.searchParams.set('amount', rawAmount.toString());
    freshUrl.searchParams.set('taker', takerAddress);
    if (slippageBps !== 'auto') {
      freshUrl.searchParams.set('slippageBps', slippageBps.toString());
    }
    const freshResponse = await apiFetch(freshUrl.toString());
    if (!freshResponse.ok) {
      const errData = await freshResponse.json().catch(() => ({}));
      throw new Error(errData.error || errData.message || 'Failed to get fresh quote');
    }
    return freshResponse.json();
  }, [amount, inputDecimals, inputMint, outputMint, slippageBps]);

  // POST a signed tx to Jupiter's execute endpoint, surface the result.
  // Used by all sign paths (wallet adapter, deeplink continuation, WC).
  const submitSignedSwap = useCallback(async (signedTxBase64: string, requestId: string) => {
    setSwapStatus('sending');
    const executeResponse = await apiFetch(`${getApiHost()}/api/jupiter/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signedTransaction: signedTxBase64, requestId }),
    });
    if (!executeResponse.ok) {
      const errorData = await executeResponse.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || 'Swap execution failed');
    }
    const result = await executeResponse.json();
    setTxSignature(result.signature || result.txid);
    if (result.status === 'Failed') {
      throw new Error(result.error || 'Transaction failed on-chain. Try increasing slippage.');
    }
    setSwapStatus('success');
  }, []);

  // Pick up a Phantom-deeplink-tx continuation on mount: when Phantom
  // redirects back to this token page after signing the swap tx, the
  // URL has ?phantom-tx-pending=1 and the signed tx is in localStorage.
  // The Jupiter requestId we stashed alongside lets us re-execute.
  const continuationRanRef = useRef(false);
  useEffect(() => {
    if (continuationRanRef.current) return;
    if (!new URLSearchParams(window.location.search).has('phantom-tx-pending')) return;
    continuationRanRef.current = true;

    const tx = consumePhantomTxResult('jupiter-swap');
    if (!tx) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('phantom-tx-pending');
    window.history.replaceState({}, '', url.toString());

    if (tx.error) {
      setSwapError(tx.error.toLowerCase().includes('cancel') ? 'Transaction cancelled' : tx.error);
      setSwapStatus('error');
      return;
    }
    if (!tx.signedTransactionBase58) {
      setSwapError('Phantom returned no signed transaction');
      setSwapStatus('error');
      return;
    }
    const requestId = String(tx.params.requestId || '');
    if (!requestId) {
      setSwapError('Lost the swap request ID — please retry');
      setSwapStatus('error');
      return;
    }
    const txBytes = bs58.decode(tx.signedTransactionBase58);
    const base64 = Buffer.from(txBytes).toString('base64');
    setSwapLoading(true);
    setSwapError(null);
    submitSignedSwap(base64, requestId).catch(err => {
      let msg = err?.message || 'Swap failed';
      if (msg.includes('insufficient') || msg.includes('Insufficient')) msg = 'Insufficient balance';
      else if (msg.includes('slippage') || msg.includes('Slippage')) msg = 'Slippage exceeded — try increasing tolerance';
      else if (msg.includes('rejected') || msg.includes('User rejected')) msg = 'Transaction rejected by wallet';
      setSwapError(msg);
      setSwapStatus('error');
    }).finally(() => setSwapLoading(false));
  }, [submitSignedSwap]);

  const handleSwap = async () => {
    if (!quote) return;

    // Mobile fast path #1: persistent Phantom session for the auth wallet
    // → bounce to Phantom for tx approval (page reload + on-mount
    // continuation above resumes when Phantom redirects back).
    const usePhantomDeeplink = !publicKey && persistentPhantomWalletMatches(authWalletAddress);
    if (usePhantomDeeplink) {
      try {
        setSwapLoading(true);
        setSwapError(null);
        setSwapStatus('signing');
        const freshOrder = await fetchFreshOrder(authWalletAddress!);
        const txBytes = Buffer.from(freshOrder.transaction, 'base64');
        // Phantom's signTransaction expects base58. The Jupiter tx is
        // a versioned tx, but Phantom signs the raw bytes either way.
        const transactionBase58 = bs58.encode(txBytes);
        startPhantomSignTransaction({
          transactionBase58,
          operation: 'jupiter-swap',
          params: { requestId: freshOrder.requestId },
          returnUrl: window.location.pathname + window.location.search,
        });
        // Page navigates away; control resumes via the useEffect above
        // when Phantom redirects to /auth/phantom-callback then here.
        return;
      } catch (err: any) {
        setSwapError(err?.message || 'Swap failed');
        setSwapStatus('error');
        setSwapLoading(false);
        return;
      }
    }

    // Mobile fast path #2: live WC session for the auth wallet → sign
    // over the relay, await on the same page (no continuation needed).
    const useWalletConnect = !publicKey && !!authWalletAddress && hasLiveWCSessionSync(authWalletAddress);
    if (useWalletConnect) {
      setSwapLoading(true);
      setSwapError(null);
      setSwapStatus('signing');
      try {
        const freshOrder = await fetchFreshOrder(authWalletAddress!);
        const txBytes = Buffer.from(freshOrder.transaction, 'base64');
        const transactionBase58 = bs58.encode(txBytes);
        const signedBytes = await signTransactionViaWC(transactionBase58);
        const base64 = Buffer.from(signedBytes).toString('base64');
        await submitSignedSwap(base64, freshOrder.requestId);
      } catch (err: any) {
        let msg = err?.message || 'Swap failed';
        if (msg.includes('insufficient') || msg.includes('Insufficient')) msg = 'Insufficient balance';
        else if (msg.includes('slippage') || msg.includes('Slippage')) msg = 'Slippage exceeded — try increasing tolerance';
        else if (msg.includes('rejected') || msg.toLowerCase().includes('cancel')) msg = 'Transaction rejected by wallet';
        setSwapError(msg);
        setSwapStatus('error');
      } finally {
        setSwapLoading(false);
      }
      return;
    }

    // Desktop / extension wallet path.
    if (!signTransaction || !publicKey) {
      // Mobile + authenticated but no live wallet sign session matches —
      // typical when the user signed in via something OTHER than the
      // Phantom-deeplink or WC paths (older cached sign-in, wallet
      // session cleared by a wallet-mismatch login, etc). Bypass the
      // picker (which would trigger a redundant sign-in flow) and bounce
      // straight to Phantom's in-app browser. Inside Phantom's WebView
      // the standard wallet-adapter signTransaction works against
      // window.solana — the user can complete the swap there. Cookie
      // jars are separate from Safari, so they may need to sign in
      // again inside Phantom; that's a known tradeoff vs. the cleaner
      // deeplink-tx path which requires a cached Phantom session.
      if (authWalletAddress && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        window.location.href = buildWalletBounceUrl('phantom', 'swap');
        return;
      }
      if (redirectToPhantomIfMobile('swap')) return;
      setWalletModalVisible(true);
      return;
    }

    setSwapLoading(true);
    setSwapError(null);
    setSwapStatus('signing');

    try {
      const freshOrder = await fetchFreshOrder(publicKey.toBase58());
      const transactionBuffer = Buffer.from(freshOrder.transaction, 'base64');
      const transaction = VersionedTransaction.deserialize(transactionBuffer);
      const signedTransaction = await signTransaction(transaction);

      setSwapStatus('sending');

      const signedTxBase64 = Buffer.from(signedTransaction.serialize()).toString('base64');

      const executeResponse = await apiFetch(`${getApiHost()}/api/jupiter/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          signedTransaction: signedTxBase64,
          requestId: freshOrder.requestId,
        }),
      });

      if (!executeResponse.ok) {
        const errorData = await executeResponse.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Swap execution failed');
      }

      const result = await executeResponse.json();
      setTxSignature(result.signature || result.txid);

      // Jupiter /execute returns { status: "Success" | "Failed" } even on HTTP 200
      if (result.status === 'Failed') {
        throw new Error(result.error || 'Transaction failed on-chain. Try increasing slippage.');
      }

      setSwapStatus('success');
    } catch (err: any) {
      console.error('Swap error:', err);
      let msg = err.message || 'Swap failed';
      if (msg.includes('insufficient') || msg.includes('Insufficient')) msg = 'Insufficient balance';
      else if (msg.includes('slippage') || msg.includes('Slippage')) msg = 'Slippage exceeded — try increasing tolerance';
      else if (msg.includes('rejected') || msg.includes('User rejected')) msg = 'Transaction rejected by wallet';
      setSwapError(msg);
      setSwapStatus('error');
    } finally {
      setSwapLoading(false);
    }
  };

  const formatOutput = (raw: string, decimals: number): string => {
    const value = parseFloat(raw) / Math.pow(10, decimals);
    if (value >= 1_000_000) return (value / 1_000_000).toFixed(2) + 'M';
    if (value >= 1_000) return (value / 1_000).toFixed(2) + 'K';
    if (value >= 1) return value.toFixed(4);
    return value.toPrecision(4);
  };

  const getRouteLabel = (): string => {
    if (!quote?.routePlan) return 'Jupiter';
    const labels = quote.routePlan.map(r => r.swapInfo.label).filter(Boolean);
    return [...new Set(labels)].join(' → ') || 'Jupiter';
  };

  // Success state — "New swap" is the primary action (most users
  // want to keep trading); the Solscan link is a secondary
  // confirmation/inspection link below.
  if (swapStatus === 'success') {
    return (
      <div className={`bg-crypto-card h-full flex flex-col items-center justify-center ${compact ? 'p-4' : 'rounded-xl p-5'}`}>
        <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
          <svg className="w-7 h-7 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-white mb-1">
          {mode === 'buy' ? 'Buy' : 'Sell'} Successful
        </h3>
        <p className="text-gray-400 text-sm mb-5">Transaction submitted</p>
        <button
          onClick={() => { setSwapStatus('idle'); setTxSignature(null); setQuote(null); }}
          className="w-full max-w-[280px] bg-green-500 hover:bg-green-400 text-black font-semibold py-2.5 px-4 rounded-lg transition-colors mb-3"
        >
          New swap
        </button>
        {txSignature && (
          <a
            href={`https://solscan.io/tx/${txSignature}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-300 text-xs transition-colors"
          >
            View on Solscan
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-crypto-card flex flex-col h-full ${compact ? 'p-3 rounded-none' : 'p-5 rounded-xl'}`}>
      {/* Buy / Sell Tabs */}
      <div className={`flex rounded-lg bg-crypto-dark p-1 ${compact ? 'mb-2' : 'mb-4'}`}>
        <button
          onClick={() => setMode('buy')}
          className={`flex-1 ${compact ? 'py-1.5 text-xs' : 'py-2 text-sm'} font-semibold rounded-md transition-colors border ${
            mode === 'buy'
              ? 'border-green-500 text-green-400'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Buy
        </button>
        <button
          onClick={() => setMode('sell')}
          className={`flex-1 ${compact ? 'py-1.5 text-xs' : 'py-2 text-sm'} font-semibold rounded-md transition-colors border ${
            mode === 'sell'
              ? 'border-red-500 text-red-400'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Sell
        </button>
      </div>

      {/* Risk Warning (buy mode only) */}
      {isRisky && mode === 'buy' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 mb-3">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-red-300/80 text-xs">
              Flagged as <span className="font-semibold text-red-400">{riskLevel}</span> — proceed with caution
            </p>
          </div>
        </div>
      )}

      {/* Amount Input — compact mode collapses Amount label, input,
          and unit symbol into a single horizontal row inside the
          input border. Saves ~40px of vertical height. */}
      <div className={compact ? 'mb-2' : 'mb-3'}>
        {!compact && (
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-gray-400 text-xs font-medium">Amount</label>
            <span className="text-gray-500 text-xs">
              {inputSymbol}
              {mode === 'sell' && tokenBalance != null && (
                <span className="ml-1 text-gray-600">
                  (bal: {tokenBalance >= 1000 ? (tokenBalance / 1000).toFixed(1) + 'K' : tokenBalance.toFixed(2)})
                </span>
              )}
            </span>
          </div>
        )}
        {compact ? (
          <div className="flex items-center gap-2 bg-crypto-dark border border-gray-700 rounded-lg px-3 py-1.5 focus-within:border-gray-500 transition-colors">
            <label className="text-gray-500 text-xs font-medium flex-shrink-0">Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.0"
              step="any"
              min="0"
              className="flex-1 min-w-0 bg-transparent border-0 outline-none text-white placeholder-gray-600 text-base font-medium text-right"
            />
            <span className="text-gray-500 text-xs flex-shrink-0">
              {inputSymbol}
              {mode === 'sell' && tokenBalance != null && (
                <span className="ml-1 text-gray-600">
                  ({tokenBalance >= 1000 ? (tokenBalance / 1000).toFixed(1) + 'K' : tokenBalance.toFixed(2)})
                </span>
              )}
            </span>
          </div>
        ) : (
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.0"
            step="any"
            min="0"
            className="w-full bg-crypto-dark border border-gray-700 rounded-lg px-3 text-white placeholder-gray-600 focus:outline-none focus:border-gray-500 transition-colors font-medium py-2.5 text-lg"
          />
        )}
        {/* Presets */}
        <div className="flex gap-1.5 mt-1.5">
          {presets.map((p) => (
            <button
              key={p.value}
              onClick={() => setAmount(p.value)}
              className={`flex-1 py-1 px-2 rounded text-xs font-medium transition-colors ${
                amount === p.value
                  ? 'bg-gray-600 text-white'
                  : 'bg-crypto-dark text-gray-400 hover:text-gray-200 border border-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Slippage */}
      <div className={compact ? 'mb-2' : 'mb-3'}>
        <button
          onClick={() => setShowSlippage(!showSlippage)}
          className="flex items-center gap-1 text-gray-500 hover:text-gray-300 text-xs transition-colors"
        >
          <svg className={`w-3 h-3 transition-transform ${showSlippage ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Slippage: {slippageBps === 'auto' ? 'Auto' : `${slippageBps / 100}%`}
        </button>
        {showSlippage && (
          <div className="flex gap-1.5 mt-1.5">
            {[
              { label: 'Auto', value: 'auto' as const },
              { label: '1%', value: 100 as const },
              { label: '3%', value: 300 as const },
              { label: '5%', value: 500 as const },
            ].map((opt) => (
              <button
                key={String(opt.value)}
                onClick={() => setSlippageBps(opt.value)}
                className={`flex-1 py-1 px-2 rounded text-xs font-medium transition-colors ${
                  slippageBps === opt.value
                    ? 'bg-gray-600 text-white'
                    : 'bg-crypto-dark text-gray-400 hover:text-gray-200 border border-gray-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quote Display. Always rendered: pre-connect, fetchQuote
          falls back to the /quote (price-only) endpoint so the user
          can see what they'd get before opening the wallet modal.
          Once a wallet is available we re-quote against /order to
          get a signable transaction, transparently. */}
      <div
        className={`bg-crypto-dark rounded-lg flex-shrink-0 ${compact ? 'p-2 mb-2' : 'p-3 mb-3'}`}
          // Reserve enough height for the tallest variant of this
          // box (You receive + Route, sometimes + Price impact). In
          // compact mode this stops the action button from jumping
          // when the user goes from "Enter an amount" → quote loaded.
          style={compact ? { minHeight: '72px' } : undefined}
        >
          {quoteLoading ? (
            <div className="flex items-center justify-center gap-2 py-1">
              <div className="w-3.5 h-3.5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-gray-500 text-xs">Fetching quote…</span>
            </div>
          ) : quoteError ? (
            <p className="text-gray-500 text-xs text-center py-1">{quoteError}</p>
          ) : quote ? (
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-xs">You receive (est.)</span>
                <span className="text-white text-sm font-semibold">
                  {formatOutput(quote.outAmount, outputDecimals)} {outputSymbol}
                </span>
              </div>
              {quote.priceImpactPct && parseFloat(quote.priceImpactPct) > 0.01 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-xs">Price impact</span>
                  <span className={`text-xs font-medium ${
                    parseFloat(quote.priceImpactPct) > 5 ? 'text-red-400' :
                    parseFloat(quote.priceImpactPct) > 1 ? 'text-yellow-400' :
                    'text-gray-400'
                  }`}>
                    {parseFloat(quote.priceImpactPct).toFixed(2)}%
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-xs">Route</span>
                <span className="text-gray-400 text-xs">{getRouteLabel()}</span>
              </div>
            </div>
          ) : (
            <p className="text-gray-600 text-xs text-center py-1">
              Enter an amount to see quote
            </p>
          )}
      </div>

      {/* Swap Error */}
      {swapError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 mb-3">
          <p className="text-red-400 text-xs">{swapError}</p>
          {txSignature && (
            <a
              href={`https://solscan.io/tx/${txSignature}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-300 text-xs mt-1 inline-block transition-colors"
            >
              View tx: {txSignature.slice(0, 8)}…
            </a>
          )}
        </div>
      )}

      {/* Spacer pushes the button to the bottom of the panel on
          desktop (where the panel stretches to match the chart height).
          In compact mode (mobile fullscreen drawer) the panel is
          already height-fit, so the spacer just produces dead space
          between the quote and the button — skip it. */}
      {!compact && <div className="flex-1" />}

      {/* Action Button. Single Buy/Sell affordance; handleSwap routes
          a disconnected user through the wallet modal (or Phantom
          deeplink / WC on mobile) before signing, so the user model
          is just "click Buy". Disabled until we have a quote, which
          requires only an amount (the /quote fallback runs without
          a wallet). */}
      <button
          onClick={handleSwap}
          disabled={!quote || swapLoading || quoteLoading}
          className={`w-full flex items-center justify-center gap-2 font-semibold px-4 rounded-lg transition-colors ${compact ? 'py-2.5 text-sm' : 'py-3'} disabled:opacity-40 disabled:cursor-not-allowed ${
            mode === 'buy'
              ? 'bg-green-500 text-black hover:bg-green-400'
              : 'bg-red-500 text-white hover:bg-red-400'
          }`}
        >
          {swapLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
              {swapStatus === 'signing' ? 'Confirm in wallet…' : 'Submitting…'}
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              {mode === 'buy' ? 'Buy' : 'Sell'} {tokenSymbol}
            </>
          )}
      </button>

      {/* Powered by — sits naturally right after the action button.
          The drawer height is sized to fit the full panel content so
          there's no extra space to fill, and mt-auto here was causing
          the visible "scrollable area sliding around" since it pushed
          Jupiter to the drawer's bottom while the natural content
          height was actually overflowing the drawer. */}
      <p className="text-center text-gray-600 text-[10px] mt-2">
        Powered by Jupiter
      </p>
    </div>
  );
};

export default SwapPanel;
